// src/modules/Admin/pages/MarketIntelligence/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import {
  collectNews,
  collectIBGE,
  collectCompetitors,
  collectAll
} from '../../../../services/marketIntelligence';
import {
  FaBell,
  FaChartLine,
  FaNewspaper,
  FaBuilding,
  FaSearch,
  FaPlus,
  FaExternalLinkAlt,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaInfoCircle,
  FaArrowUp,
  FaArrowDown,
  FaSync,
  FaTrash,
  FaEdit,
  FaCog,
  FaIndustry,
  FaGlobe,
  FaPlay,
  FaSpinner,
  FaWhatsapp,
  FaUpload,
  FaLightbulb,
  FaUsers,
  FaRocket,
  FaFileAlt
} from 'react-icons/fa';
import { analyzeWhatsAppChat } from '../../../../services/aiService';

// Styled Components
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #8b5cf6;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.$primary ? '#111' : 'white'};
  color: ${props => props.$primary ? 'white' : '#333'};
  border: 1px solid ${props => props.$primary ? '#111' : '#e5e5e5'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$primary ? '#333' : '#f5f5f5'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 4px;
  background: #f5f5f5;
  padding: 4px;
  border-radius: 10px;
  margin-bottom: 24px;
  overflow-x: auto;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#111' : '#666'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;
  white-space: nowrap;
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};

  &:hover {
    color: #111;
  }

  .badge {
    background: ${props => props.$active ? '#ef4444' : '#999'};
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 10px;
    min-width: 18px;
    text-align: center;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${props => props.$color || '#f5f5f5'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #666;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #666;
  }
`;

const CardContent = styled.div`
  padding: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;

  svg {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    margin-bottom: 20px;
  }
`;

// Alerts List Styles
const AlertsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AlertItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  background: ${props => props.$unread ? '#fafafa' : 'white'};
  border: 1px solid ${props => {
    switch(props.$severity) {
      case 'critical': return '#fecaca';
      case 'high': return '#fed7aa';
      case 'medium': return '#fef08a';
      default: return '#e5e5e5';
    }
  }};
  border-radius: 8px;
  transition: all 200ms;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const AlertIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => {
    switch(props.$severity) {
      case 'critical': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      default: return '#6b7280';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
`;

const AlertContent = styled.div`
  flex: 1;
  min-width: 0;

  .title {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    margin-bottom: 4px;
  }

  .description {
    font-size: 13px;
    color: #666;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .meta {
    font-size: 12px;
    color: #999;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const AlertActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e5e5e5;
  background: white;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #f5f5f5;
    color: #111;
  }
`;

// Trends Chart Placeholder
const TrendsChart = styled.div`
  height: 200px;
  background: linear-gradient(180deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0) 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;
`;

const TrendsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TrendItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
`;

const TrendInfo = styled.div`
  .keyword {
    font-size: 14px;
    font-weight: 500;
    color: #111;
    margin-bottom: 2px;
  }

  .source {
    font-size: 12px;
    color: #666;
  }
`;

const TrendValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .value {
    font-size: 14px;
    font-weight: 600;
    color: #111;
  }

  .change {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 500;
    color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  }
`;

// News List Styles
const NewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NewsItem = styled.a`
  display: block;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  text-decoration: none;
  transition: all 200ms;

  &:hover {
    background: #f0f0f0;
  }

  .title {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: #666;
  }
`;

const SentimentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    switch(props.$sentiment) {
      case 'positive': return 'rgba(16, 185, 129, 0.1)';
      case 'negative': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  }};
  color: ${props => {
    switch(props.$sentiment) {
      case 'positive': return '#10b981';
      case 'negative': return '#ef4444';
      default: return '#6b7280';
    }
  }};
`;

// Competitors Styles
const CompetitorsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CompetitorItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
`;

const CompetitorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: ${props => props.$color || '#e5e5e5'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
`;

const CompetitorInfo = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    margin-bottom: 4px;
  }

  .website {
    font-size: 12px;
    color: #666;
  }
`;

const CompetitorStats = styled.div`
  text-align: right;

  .updates {
    font-size: 14px;
    font-weight: 600;
    color: #111;
  }

  .label {
    font-size: 11px;
    color: #666;
  }
`;

// Config List Styles
const ConfigList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ConfigItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${props => props.$active ? '#f9f9f9' : '#fff5f5'};
  border: 1px solid ${props => props.$active ? '#e5e5e5' : '#fecaca'};
  border-radius: 8px;

  .info {
    display: flex;
    align-items: center;
    gap: 12px;

    .type {
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      color: #666;
      background: #e5e5e5;
      padding: 2px 6px;
      border-radius: 4px;
    }

    .name {
      font-size: 14px;
      font-weight: 500;
      color: #111;
    }
  }

  .status {
    font-size: 12px;
    color: ${props => props.$active ? '#10b981' : '#ef4444'};
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #111;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
  }

  input, select, textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 14px;
    transition: border-color 200ms;

    &:focus {
      outline: none;
      border-color: #111;
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
`;

const MarketIntelligence = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data states
  const [alerts, setAlerts] = useState([]);
  const [trends, setTrends] = useState([]);
  const [news, setNews] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [stats, setStats] = useState({
    unreadAlerts: 0,
    totalTrends: 0,
    totalNews: 0,
    activeCompetitors: 0
  });

  // Modal states
  const [showCompetitorModal, setShowCompetitorModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({
    name: '',
    website: '',
    linkedin_url: '',
    description: ''
  });
  const [newConfig, setNewConfig] = useState({
    config_type: 'keyword',
    name: '',
    value: '',
    check_frequency: 'daily'
  });

  // Collection states
  const [collecting, setCollecting] = useState({
    all: false,
    news: false,
    ibge: false,
    competitors: false
  });
  const [collectionResult, setCollectionResult] = useState(null);

  // WhatsApp states
  const [whatsappAnalysis, setWhatsappAnalysis] = useState(null);
  const [analyzingWhatsapp, setAnalyzingWhatsapp] = useState(false);
  const [whatsappProgress, setWhatsappProgress] = useState({ stage: '', message: '' });
  const [whatsappHistory, setWhatsappHistory] = useState([]);
  const [loadingWhatsappHistory, setLoadingWhatsappHistory] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Carregar histórico de análises WhatsApp quando mudar para a tab
  useEffect(() => {
    if (activeTab === 'whatsapp') {
      fetchWhatsappHistory();
    }
  }, [activeTab]);

  const fetchWhatsappHistory = async () => {
    setLoadingWhatsappHistory(true);
    try {
      const { data, error } = await supabase
        .from('whatsapp_analysis')
        .select('*')
        .order('analyzed_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setWhatsappHistory(data || []);
    } catch (error) {
      console.log('Erro ao carregar histórico WhatsApp:', error.message);
    } finally {
      setLoadingWhatsappHistory(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch alerts
      const { data: alertsData } = await supabase
        .from('market_alerts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      // Fetch trends
      const { data: trendsData } = await supabase
        .from('market_trends')
        .select('*')
        .order('date', { ascending: false })
        .limit(20);

      // Fetch news
      const { data: newsData } = await supabase
        .from('market_news')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(10);

      // Fetch competitors
      const { data: competitorsData } = await supabase
        .from('competitors')
        .select('*, competitor_updates(count)')
        .eq('is_active', true);

      // Fetch configs
      const { data: configsData } = await supabase
        .from('monitoring_config')
        .select('*')
        .order('config_type', { ascending: true });

      setAlerts(alertsData || []);
      setTrends(trendsData || []);
      setNews(newsData || []);
      setCompetitors(competitorsData || []);
      setConfigs(configsData || []);

      // Calculate stats
      setStats({
        unreadAlerts: (alertsData || []).filter(a => !a.is_read).length,
        totalTrends: (trendsData || []).length,
        totalNews: (newsData || []).length,
        activeCompetitors: (competitorsData || []).length
      });

    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // Funções de coleta de dados
  const handleCollectAll = async () => {
    setCollecting(prev => ({ ...prev, all: true }));
    setCollectionResult(null);
    try {
      const result = await collectAll();
      setCollectionResult({ type: 'success', message: 'Coleta completa executada!', data: result });
      await fetchData(); // Atualiza os dados
    } catch (error) {
      setCollectionResult({ type: 'error', message: error.message });
    } finally {
      setCollecting(prev => ({ ...prev, all: false }));
    }
  };

  const handleCollectNews = async () => {
    setCollecting(prev => ({ ...prev, news: true }));
    setCollectionResult(null);
    try {
      const queries = configs
        .filter(c => c.config_type === 'news_query' && c.is_active)
        .map(c => c.value);
      const result = await collectNews(queries.length > 0 ? queries : null);
      setCollectionResult({
        type: 'success',
        message: `Notícias coletadas: ${result.stats?.inserted || 0} novas`,
        data: result
      });
      await fetchData();
    } catch (error) {
      setCollectionResult({ type: 'error', message: error.message });
    } finally {
      setCollecting(prev => ({ ...prev, news: false }));
    }
  };

  const handleCollectIBGE = async () => {
    setCollecting(prev => ({ ...prev, ibge: true }));
    setCollectionResult(null);
    try {
      const result = await collectIBGE();
      setCollectionResult({
        type: 'success',
        message: 'Indicadores IBGE atualizados!',
        data: result
      });
      await fetchData();
    } catch (error) {
      setCollectionResult({ type: 'error', message: error.message });
    } finally {
      setCollecting(prev => ({ ...prev, ibge: false }));
    }
  };

  const handleCollectCompetitors = async () => {
    setCollecting(prev => ({ ...prev, competitors: true }));
    setCollectionResult(null);
    try {
      const result = await collectCompetitors();
      setCollectionResult({
        type: 'success',
        message: `Concorrentes verificados: ${result.stats?.checked || 0}`,
        data: result
      });
      await fetchData();
    } catch (error) {
      setCollectionResult({ type: 'error', message: error.message });
    } finally {
      setCollecting(prev => ({ ...prev, competitors: false }));
    }
  };

  const isCollecting = Object.values(collecting).some(v => v);

  const markAlertAsRead = async (alertId) => {
    try {
      await supabase
        .from('market_alerts')
        .update({ is_read: true })
        .eq('id', alertId);

      setAlerts(alerts.map(a =>
        a.id === alertId ? { ...a, is_read: true } : a
      ));
      setStats(prev => ({ ...prev, unreadAlerts: prev.unreadAlerts - 1 }));
    } catch (error) {
      console.error('Erro ao marcar alerta:', error);
    }
  };

  const addCompetitor = async () => {
    try {
      const { data, error } = await supabase
        .from('competitors')
        .insert([newCompetitor])
        .select()
        .single();

      if (error) throw error;

      setCompetitors([...competitors, data]);
      setShowCompetitorModal(false);
      setNewCompetitor({ name: '', website: '', linkedin_url: '', description: '' });
    } catch (error) {
      console.error('Erro ao adicionar concorrente:', error);
    }
  };

  const addConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('monitoring_config')
        .insert([{
          ...newConfig,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;

      setConfigs([...configs, data]);
      setShowConfigModal(false);
      setNewConfig({ config_type: 'keyword', name: '', value: '', check_frequency: 'daily' });
    } catch (error) {
      console.error('Erro ao adicionar config:', error);
    }
  };

  const deleteConfig = async (configId) => {
    if (!window.confirm('Tem certeza que deseja remover este monitoramento?')) return;

    try {
      await supabase
        .from('monitoring_config')
        .delete()
        .eq('id', configId);

      setConfigs(configs.filter(c => c.id !== configId));
    } catch (error) {
      console.error('Erro ao deletar config:', error);
    }
  };

  // WhatsApp Analysis Handler
  const handleWhatsAppFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Verifica se é arquivo de texto
    if (!file.name.endsWith('.txt')) {
      alert('Por favor, envie um arquivo .txt exportado do WhatsApp');
      return;
    }

    setAnalyzingWhatsapp(true);
    setWhatsappAnalysis(null);

    try {
      // Lê o conteúdo do arquivo
      const content = await file.text();

      // Analisa com IA
      const result = await analyzeWhatsAppChat(content, (stage, message) => {
        setWhatsappProgress({ stage, message });
      });

      setWhatsappAnalysis(result);

      // Salvar no Supabase
      try {
        const { data: savedAnalysis, error } = await supabase
          .from('whatsapp_analysis')
          .insert([{
            filename: file.name,
            estrutura: result.estrutura,
            topicos: result.topicos,
            insights: result.insights,
            periodo: result.estrutura?.periodo,
            total_mensagens: result.estrutura?.total_mensagens,
            participantes_count: result.estrutura?.participantes_ativos?.length || 0,
            analyzed_at: result.analyzed_at
          }])
          .select()
          .single();

        if (error) throw error;

        // Atualizar histórico com a análise salva
        setWhatsappHistory(prev => [savedAnalysis, ...prev]);
        console.log('Análise salva com sucesso:', savedAnalysis.id);
      } catch (e) {
        console.error('Erro ao salvar análise:', e.message);
        // Salvar localmente mesmo se falhar no Supabase
        const localRecord = {
          id: Date.now().toString(),
          filename: file.name,
          analyzed_at: result.analyzed_at,
          estrutura: result.estrutura,
          topicos: result.topicos,
          insights: result.insights
        };
        setWhatsappHistory(prev => [localRecord, ...prev]);
      }

    } catch (error) {
      console.error('Erro ao analisar WhatsApp:', error);
      alert('Erro ao analisar arquivo: ' + error.message);
    } finally {
      setAnalyzingWhatsapp(false);
      setWhatsappProgress({ stage: '', message: '' });
    }
  };

  // Visualizar análise do histórico
  const handleViewAnalysis = (analysis) => {
    // Garantir que os campos JSON sejam objetos (podem vir como string do Supabase)
    const parseIfString = (data) => {
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch {
          return data;
        }
      }
      return data;
    };

    setWhatsappAnalysis({
      id: analysis.id,
      estrutura: parseIfString(analysis.estrutura),
      topicos: parseIfString(analysis.topicos),
      insights: parseIfString(analysis.insights),
      analyzed_at: analysis.analyzed_at
    });
  };

  // Deletar análise
  const handleDeleteAnalysis = async (analysisId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta análise?')) return;

    try {
      const { error } = await supabase
        .from('whatsapp_analysis')
        .delete()
        .eq('id', analysisId);

      if (error) throw error;

      setWhatsappHistory(prev => prev.filter(a => a.id !== analysisId));

      // Se a análise atual foi deletada, limpar
      if (whatsappAnalysis?.id === analysisId) {
        setWhatsappAnalysis(null);
      }
    } catch (error) {
      console.error('Erro ao deletar análise:', error);
      alert('Erro ao deletar análise');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getAlertIcon = (type) => {
    switch(type) {
      case 'trend_spike': return FaChartLine;
      case 'competitor_move': return FaBuilding;
      case 'news_important': return FaNewspaper;
      case 'indicator_change': return FaIndustry;
      default: return FaBell;
    }
  };

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: FaChartLine },
    { id: 'whatsapp', label: 'WhatsApp Intel', icon: FaWhatsapp },
    { id: 'alerts', label: 'Alertas', icon: FaBell, badge: stats.unreadAlerts },
    { id: 'trends', label: 'Tendências', icon: FaSearch },
    { id: 'news', label: 'Notícias', icon: FaNewspaper },
    { id: 'competitors', label: 'Concorrentes', icon: FaBuilding },
    { id: 'config', label: 'Configurações', icon: FaCog },
  ];

  const statsCards = [
    { icon: FaBell, label: 'Alertas Não Lidos', value: stats.unreadAlerts, color: '#ef4444' },
    { icon: FaChartLine, label: 'Tendências', value: stats.totalTrends, color: '#8b5cf6' },
    { icon: FaNewspaper, label: 'Notícias Coletadas', value: stats.totalNews, color: '#3b82f6' },
    { icon: FaBuilding, label: 'Concorrentes Ativos', value: stats.activeCompetitors, color: '#10b981' },
  ];

  const competitorColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  return (
    <Container>
      <PageHeader>
        <PageTitle>
          <FaGlobe />
          Inteligência de Mercado
        </PageTitle>
        <HeaderActions>
          <ActionButton onClick={handleRefresh} disabled={refreshing || isCollecting}>
            <FaSync className={refreshing ? 'fa-spin' : ''} />
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </ActionButton>
          <ActionButton $primary onClick={handleCollectAll} disabled={isCollecting}>
            {collecting.all ? <FaSpinner className="fa-spin" /> : <FaPlay />}
            {collecting.all ? 'Coletando...' : 'Coletar Dados'}
          </ActionButton>
        </HeaderActions>
      </PageHeader>

      {/* Collection Result Banner */}
      {collectionResult && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '24px',
          borderRadius: '8px',
          background: collectionResult.type === 'success' ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${collectionResult.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
          color: collectionResult.type === 'success' ? '#065f46' : '#991b1b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{collectionResult.message}</span>
          <button
            onClick={() => setCollectionResult(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <FaTimes />
          </button>
        </div>
      )}

      <TabsContainer>
        {tabs.map(tab => (
          <Tab
            key={tab.id}
            $active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon />
            {tab.label}
            {tab.badge > 0 && <span className="badge">{tab.badge}</span>}
          </Tab>
        ))}
      </TabsContainer>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          <StatsGrid>
            {statsCards.map((stat, index) => (
              <StatCard key={index}>
                <StatHeader>
                  <StatIcon $color={stat.color}>
                    <stat.icon />
                  </StatIcon>
                </StatHeader>
                <StatValue>{stat.value}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>

          <ContentGrid>
            <Card>
              <CardHeader>
                <CardTitle>
                  <FaBell />
                  Alertas Recentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <EmptyState>
                    <FaBell />
                    <h3>Nenhum alerta ainda</h3>
                    <p>Os alertas aparecerão aqui quando forem detectadas mudanças importantes no mercado.</p>
                  </EmptyState>
                ) : (
                  <AlertsList>
                    {alerts.slice(0, 5).map(alert => {
                      const AlertIconComponent = getAlertIcon(alert.alert_type);
                      return (
                        <AlertItem key={alert.id} $severity={alert.severity} $unread={!alert.is_read}>
                          <AlertIcon $severity={alert.severity}>
                            <AlertIconComponent />
                          </AlertIcon>
                          <AlertContent>
                            <div className="title">{alert.title}</div>
                            <div className="description">{alert.description}</div>
                            <div className="meta">
                              <span>{formatDate(alert.created_at)}</span>
                              <span>{alert.alert_type.replace('_', ' ')}</span>
                            </div>
                          </AlertContent>
                          {!alert.is_read && (
                            <AlertActions>
                              <IconButton onClick={() => markAlertAsRead(alert.id)} title="Marcar como lido">
                                <FaCheck />
                              </IconButton>
                            </AlertActions>
                          )}
                        </AlertItem>
                      );
                    })}
                  </AlertsList>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <FaBuilding />
                  Concorrentes
                </CardTitle>
                <ActionButton onClick={() => setShowCompetitorModal(true)}>
                  <FaPlus /> Adicionar
                </ActionButton>
              </CardHeader>
              <CardContent>
                {competitors.length === 0 ? (
                  <EmptyState>
                    <FaBuilding />
                    <h3>Nenhum concorrente cadastrado</h3>
                    <p>Adicione concorrentes para monitorar suas atividades.</p>
                  </EmptyState>
                ) : (
                  <CompetitorsList>
                    {competitors.map((comp, index) => (
                      <CompetitorItem key={comp.id}>
                        <CompetitorAvatar $color={competitorColors[index % competitorColors.length]}>
                          {comp.name.charAt(0).toUpperCase()}
                        </CompetitorAvatar>
                        <CompetitorInfo>
                          <div className="name">{comp.name}</div>
                          <div className="website">{comp.website || 'Sem website'}</div>
                        </CompetitorInfo>
                        <CompetitorStats>
                          <div className="updates">0</div>
                          <div className="label">atualizações</div>
                        </CompetitorStats>
                      </CompetitorItem>
                    ))}
                  </CompetitorsList>
                )}
              </CardContent>
            </Card>
          </ContentGrid>
        </>
      )}

      {/* WhatsApp Intelligence Tab */}
      {activeTab === 'whatsapp' && (
        <>
          <ContentGrid style={{ gridTemplateColumns: '1fr' }}>
            {/* Upload Card */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <FaWhatsapp style={{ color: '#25D366' }} />
                  Análise de Grupo WhatsApp
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ marginBottom: '24px' }}>
                  <p style={{ color: '#666', marginBottom: '16px', lineHeight: 1.6 }}>
                    Faça upload do export de conversas do seu grupo WhatsApp (Futures ou outro).
                    Nossa IA irá extrair insights estratégicos, tendências e oportunidades de negócio.
                  </p>
                  <p style={{ fontSize: '13px', color: '#999', marginBottom: '16px' }}>
                    <strong>Como exportar:</strong> No WhatsApp, abra o grupo → Menu (⋮) → Mais → Exportar conversa → Sem mídia
                  </p>

                  <label style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    background: analyzingWhatsapp ? '#f5f5f5' : '#25D366',
                    color: analyzingWhatsapp ? '#666' : 'white',
                    borderRadius: '8px',
                    cursor: analyzingWhatsapp ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'all 200ms'
                  }}>
                    {analyzingWhatsapp ? (
                      <>
                        <FaSpinner className="fa-spin" />
                        {whatsappProgress.message || 'Analisando...'}
                      </>
                    ) : (
                      <>
                        <FaUpload />
                        Fazer Upload do Chat (.txt)
                      </>
                    )}
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleWhatsAppFileUpload}
                      disabled={analyzingWhatsapp}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                {/* Progress Indicator */}
                {analyzingWhatsapp && (
                  <div style={{
                    padding: '16px',
                    background: '#f0fdf4',
                    borderRadius: '8px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <FaSpinner className="fa-spin" style={{ color: '#25D366' }} />
                      <div>
                        <div style={{ fontWeight: '600', color: '#166534' }}>
                          {whatsappProgress.stage === 'parsing' && 'Fase 1/3: Analisando estrutura'}
                          {whatsappProgress.stage === 'topics' && 'Fase 2/3: Identificando tópicos'}
                          {whatsappProgress.stage === 'insights' && 'Fase 3/3: Extraindo insights'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#15803d' }}>
                          {whatsappProgress.message}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Histórico de Análises */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <FaFileAlt style={{ color: '#6b7280' }} />
                  Análises Anteriores
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingWhatsappHistory ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    <FaSpinner className="fa-spin" /> Carregando histórico...
                  </div>
                ) : whatsappHistory.length === 0 ? (
                  <EmptyState>
                    <FaFileAlt />
                    <h3>Nenhuma análise salva</h3>
                    <p>Faça upload de uma conversa para começar.</p>
                  </EmptyState>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {whatsappHistory.map((analysis) => (
                      <div
                        key={analysis.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '14px 16px',
                          background: '#f9f9f9',
                          borderRadius: '8px',
                          border: '1px solid #e5e5e5',
                          cursor: 'pointer',
                          transition: 'all 200ms'
                        }}
                        onClick={() => handleViewAnalysis(analysis)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            background: '#25D366',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                          }}>
                            <FaWhatsapp />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', color: '#111', marginBottom: '2px' }}>
                              {analysis.filename}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {analysis.total_mensagens ? `${analysis.total_mensagens} mensagens · ` : ''}
                              {analysis.participantes_count ? `${analysis.participantes_count} participantes · ` : ''}
                              {formatDate(analysis.analyzed_at)}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewAnalysis(analysis);
                            }}
                            title="Visualizar"
                          >
                            <FaSearch />
                          </IconButton>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAnalysis(analysis.id);
                            }}
                            title="Excluir"
                            style={{ color: '#ef4444' }}
                          >
                            <FaTrash />
                          </IconButton>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </ContentGrid>

          {/* Results */}
          {whatsappAnalysis && (
            <>
              {/* Executive Summary */}
              {whatsappAnalysis.insights?.resumo_executivo && (
                <Card style={{ marginTop: '24px' }}>
                  <CardHeader>
                    <CardTitle>
                      <FaLightbulb style={{ color: '#f59e0b' }} />
                      Resumo Executivo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p style={{ fontSize: '15px', lineHeight: 1.7, color: '#333' }}>
                      {whatsappAnalysis.insights.resumo_executivo}
                    </p>
                  </CardContent>
                </Card>
              )}

              <ContentGrid style={{ marginTop: '24px' }}>
                {/* Topics */}
                <Card>
                  <CardHeader>
                    <CardTitle>
                      <FaChartLine style={{ color: '#8b5cf6' }} />
                      Tópicos Mais Discutidos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {whatsappAnalysis.topicos?.topicos?.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {whatsappAnalysis.topicos.topicos.slice(0, 8).map((topic, idx) => (
                          <div key={idx} style={{
                            padding: '12px',
                            background: '#f9f9f9',
                            borderRadius: '8px',
                            borderLeft: `3px solid ${topic.frequencia === 'alta' ? '#ef4444' : topic.frequencia === 'média' ? '#f59e0b' : '#22c55e'}`
                          }}>
                            <div style={{ fontWeight: '600', color: '#111', marginBottom: '4px' }}>
                              {topic.titulo}
                            </div>
                            <div style={{ fontSize: '13px', color: '#666' }}>
                              {topic.resumo}
                            </div>
                            <span style={{
                              display: 'inline-block',
                              marginTop: '8px',
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              background: topic.frequencia === 'alta' ? '#fef2f2' : topic.frequencia === 'média' ? '#fffbeb' : '#f0fdf4',
                              color: topic.frequencia === 'alta' ? '#dc2626' : topic.frequencia === 'média' ? '#d97706' : '#16a34a'
                            }}>
                              Frequência {topic.frequencia}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState>
                        <p>Nenhum tópico identificado</p>
                      </EmptyState>
                    )}
                  </CardContent>
                </Card>

                {/* Technologies */}
                <Card>
                  <CardHeader>
                    <CardTitle>
                      <FaRocket style={{ color: '#3b82f6' }} />
                      Tecnologias Mencionadas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {whatsappAnalysis.topicos?.tecnologias?.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {whatsappAnalysis.topicos.tecnologias.slice(0, 10).map((tech, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            background: '#f9f9f9',
                            borderRadius: '6px'
                          }}>
                            <div>
                              <div style={{ fontWeight: '500', color: '#111' }}>{tech.nome}</div>
                              <div style={{ fontSize: '12px', color: '#666' }}>{tech.contexto}</div>
                            </div>
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#3b82f6'
                            }}>
                              ~{tech.mencoes} menções
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState>
                        <p>Nenhuma tecnologia identificada</p>
                      </EmptyState>
                    )}
                  </CardContent>
                </Card>
              </ContentGrid>

              {/* Opportunities and Content Ideas */}
              <ContentGrid style={{ marginTop: '24px' }}>
                {/* Prospection Opportunities */}
                <Card>
                  <CardHeader>
                    <CardTitle>
                      <FaUsers style={{ color: '#10b981' }} />
                      Oportunidades de Prospecção
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {whatsappAnalysis.insights?.oportunidades_prospeccao?.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {whatsappAnalysis.insights.oportunidades_prospeccao.map((opp, idx) => (
                          <div key={idx} style={{
                            padding: '14px',
                            background: opp.prioridade === 'alta' ? '#fef2f2' : '#f9f9f9',
                            borderRadius: '8px',
                            border: opp.prioridade === 'alta' ? '1px solid #fecaca' : '1px solid #e5e5e5'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', color: '#111', marginBottom: '6px' }}>
                                  {opp.insight}
                                </div>
                                <div style={{ fontSize: '13px', color: '#666' }}>
                                  <strong>Ação:</strong> {opp.acao_sugerida}
                                </div>
                              </div>
                              <span style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                padding: '3px 8px',
                                borderRadius: '10px',
                                background: opp.prioridade === 'alta' ? '#ef4444' : opp.prioridade === 'média' ? '#f59e0b' : '#22c55e',
                                color: 'white',
                                whiteSpace: 'nowrap'
                              }}>
                                {opp.prioridade?.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState>
                        <p>Nenhuma oportunidade identificada</p>
                      </EmptyState>
                    )}
                  </CardContent>
                </Card>

                {/* Content Ideas */}
                <Card>
                  <CardHeader>
                    <CardTitle>
                      <FaFileAlt style={{ color: '#8b5cf6' }} />
                      Ideias de Conteúdo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {whatsappAnalysis.insights?.ideias_conteudo?.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {whatsappAnalysis.insights.ideias_conteudo.map((idea, idx) => (
                          <div key={idx} style={{
                            padding: '12px',
                            background: '#f9f9f9',
                            borderRadius: '8px'
                          }}>
                            <div style={{ fontWeight: '600', color: '#111', marginBottom: '4px' }}>
                              {idea.tema}
                            </div>
                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                              {idea.justificativa}
                            </div>
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              background: idea.formato === 'blog' ? '#dbeafe' : idea.formato === 'linkedin' ? '#e0e7ff' : '#fef3c7',
                              color: idea.formato === 'blog' ? '#1d4ed8' : idea.formato === 'linkedin' ? '#4338ca' : '#b45309'
                            }}>
                              {idea.formato}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <EmptyState>
                        <p>Nenhuma ideia de conteúdo identificada</p>
                      </EmptyState>
                    )}
                  </CardContent>
                </Card>
              </ContentGrid>

              {/* Market Gaps */}
              {whatsappAnalysis.insights?.gaps_mercado?.length > 0 && (
                <Card style={{ marginTop: '24px' }}>
                  <CardHeader>
                    <CardTitle>
                      <FaLightbulb style={{ color: '#f59e0b' }} />
                      Gaps de Mercado Identificados
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                      {whatsappAnalysis.insights.gaps_mercado.map((gap, idx) => (
                        <div key={idx} style={{
                          padding: '16px',
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fef9c3 100%)',
                          borderRadius: '8px',
                          border: '1px solid #fde68a'
                        }}>
                          <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
                            {gap.necessidade}
                          </div>
                          <div style={{ fontSize: '13px', color: '#78350f' }}>
                            <strong>Solução potencial:</strong> {gap.solucao_potencial}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Relevant Quotes */}
              {whatsappAnalysis.insights?.citacoes_relevantes?.length > 0 && (
                <Card style={{ marginTop: '24px' }}>
                  <CardHeader>
                    <CardTitle>
                      <FaInfoCircle style={{ color: '#6b7280' }} />
                      Citações Relevantes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {whatsappAnalysis.insights.citacoes_relevantes.map((quote, idx) => (
                        <div key={idx} style={{
                          padding: '16px',
                          background: '#f9f9f9',
                          borderRadius: '8px',
                          borderLeft: '3px solid #8b5cf6'
                        }}>
                          <div style={{ fontStyle: 'italic', color: '#333', marginBottom: '8px' }}>
                            "{quote.citacao}"
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {quote.autor && <span>— {quote.autor} · </span>}
                            <span style={{ color: '#8b5cf6' }}>{quote.relevancia}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Metadata */}
              <Card style={{ marginTop: '24px' }}>
                <CardHeader>
                  <CardTitle>
                    <FaCog style={{ color: '#6b7280' }} />
                    Dados da Análise
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Período</div>
                      <div style={{ fontWeight: '500' }}>{whatsappAnalysis.estrutura?.periodo || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total de Mensagens</div>
                      <div style={{ fontWeight: '500' }}>{whatsappAnalysis.estrutura?.total_mensagens || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Participantes Ativos</div>
                      <div style={{ fontWeight: '500' }}>{whatsappAnalysis.estrutura?.participantes_ativos?.length || '-'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Analisado em</div>
                      <div style={{ fontWeight: '500' }}>{formatDate(whatsappAnalysis.analyzed_at)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <Card>
          <CardHeader>
            <CardTitle>
              <FaBell />
              Todos os Alertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <EmptyState>
                <FaBell />
                <h3>Nenhum alerta</h3>
                <p>Configure monitoramentos para receber alertas automaticamente.</p>
              </EmptyState>
            ) : (
              <AlertsList>
                {alerts.map(alert => {
                  const AlertIconComponent = getAlertIcon(alert.alert_type);
                  return (
                    <AlertItem key={alert.id} $severity={alert.severity} $unread={!alert.is_read}>
                      <AlertIcon $severity={alert.severity}>
                        <AlertIconComponent />
                      </AlertIcon>
                      <AlertContent>
                        <div className="title">{alert.title}</div>
                        <div className="description">{alert.description}</div>
                        <div className="meta">
                          <span>{formatDate(alert.created_at)}</span>
                          <span>{alert.alert_type.replace('_', ' ')}</span>
                        </div>
                      </AlertContent>
                      {!alert.is_read && (
                        <AlertActions>
                          <IconButton onClick={() => markAlertAsRead(alert.id)} title="Marcar como lido">
                            <FaCheck />
                          </IconButton>
                        </AlertActions>
                      )}
                    </AlertItem>
                  );
                })}
              </AlertsList>
            )}
          </CardContent>
        </Card>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && (
        <Card>
          <CardHeader>
            <CardTitle>
              <FaSearch />
              Tendências de Busca
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TrendsChart>
              <FaChartLine style={{ marginRight: 8 }} />
              Gráfico de tendências será exibido aqui
            </TrendsChart>
            {trends.length === 0 ? (
              <EmptyState>
                <FaSearch />
                <h3>Nenhuma tendência coletada</h3>
                <p>Configure keywords para monitorar no Google Trends.</p>
              </EmptyState>
            ) : (
              <TrendsList>
                {trends.map(trend => (
                  <TrendItem key={trend.id}>
                    <TrendInfo>
                      <div className="keyword">{trend.keyword}</div>
                      <div className="source">{trend.source} - {formatDate(trend.date)}</div>
                    </TrendInfo>
                    <TrendValue $positive={true}>
                      <span className="value">{trend.value || 0}</span>
                      <span className="change">
                        <FaArrowUp />
                        -
                      </span>
                    </TrendValue>
                  </TrendItem>
                ))}
              </TrendsList>
            )}
          </CardContent>
        </Card>
      )}

      {/* News Tab */}
      {activeTab === 'news' && (
        <Card>
          <CardHeader>
            <CardTitle>
              <FaNewspaper />
              Notícias do Setor
            </CardTitle>
          </CardHeader>
          <CardContent>
            {news.length === 0 ? (
              <EmptyState>
                <FaNewspaper />
                <h3>Nenhuma notícia coletada</h3>
                <p>Configure queries de busca para coletar notícias automaticamente.</p>
              </EmptyState>
            ) : (
              <NewsList>
                {news.map(item => (
                  <NewsItem key={item.id} href={item.url} target="_blank" rel="noopener noreferrer">
                    <div className="title">{item.title}</div>
                    <div className="meta">
                      <span>{item.source}</span>
                      <span>{formatDate(item.published_at)}</span>
                      {item.sentiment && (
                        <SentimentBadge $sentiment={item.sentiment}>
                          {item.sentiment === 'positive' ? 'Positivo' :
                           item.sentiment === 'negative' ? 'Negativo' : 'Neutro'}
                        </SentimentBadge>
                      )}
                    </div>
                  </NewsItem>
                ))}
              </NewsList>
            )}
          </CardContent>
        </Card>
      )}

      {/* Competitors Tab */}
      {activeTab === 'competitors' && (
        <Card>
          <CardHeader>
            <CardTitle>
              <FaBuilding />
              Concorrentes Monitorados
            </CardTitle>
            <ActionButton $primary onClick={() => setShowCompetitorModal(true)}>
              <FaPlus /> Adicionar Concorrente
            </ActionButton>
          </CardHeader>
          <CardContent>
            {competitors.length === 0 ? (
              <EmptyState>
                <FaBuilding />
                <h3>Nenhum concorrente cadastrado</h3>
                <p>Adicione concorrentes para monitorar suas atividades, posts e novidades.</p>
                <ActionButton $primary onClick={() => setShowCompetitorModal(true)}>
                  <FaPlus /> Adicionar Primeiro Concorrente
                </ActionButton>
              </EmptyState>
            ) : (
              <CompetitorsList>
                {competitors.map((comp, index) => (
                  <CompetitorItem key={comp.id}>
                    <CompetitorAvatar $color={competitorColors[index % competitorColors.length]}>
                      {comp.name.charAt(0).toUpperCase()}
                    </CompetitorAvatar>
                    <CompetitorInfo>
                      <div className="name">{comp.name}</div>
                      <div className="website">
                        {comp.website ? (
                          <a href={comp.website} target="_blank" rel="noopener noreferrer">
                            {comp.website} <FaExternalLinkAlt size={10} />
                          </a>
                        ) : 'Sem website'}
                      </div>
                    </CompetitorInfo>
                    <CompetitorStats>
                      <div className="updates">0</div>
                      <div className="label">atualizações</div>
                    </CompetitorStats>
                  </CompetitorItem>
                ))}
              </CompetitorsList>
            )}
          </CardContent>
        </Card>
      )}

      {/* Config Tab */}
      {activeTab === 'config' && (
        <>
          {/* Botões de Coleta Manual */}
          <Card style={{ marginBottom: '24px' }}>
            <CardHeader>
              <CardTitle>
                <FaPlay />
                Executar Coleta Manual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <ActionButton onClick={handleCollectNews} disabled={isCollecting}>
                  {collecting.news ? <FaSpinner className="fa-spin" /> : <FaNewspaper />}
                  {collecting.news ? 'Coletando...' : 'Coletar Notícias'}
                </ActionButton>
                <ActionButton onClick={handleCollectIBGE} disabled={isCollecting}>
                  {collecting.ibge ? <FaSpinner className="fa-spin" /> : <FaIndustry />}
                  {collecting.ibge ? 'Coletando...' : 'Coletar IBGE'}
                </ActionButton>
                <ActionButton onClick={handleCollectCompetitors} disabled={isCollecting}>
                  {collecting.competitors ? <FaSpinner className="fa-spin" /> : <FaBuilding />}
                  {collecting.competitors ? 'Verificando...' : 'Verificar Concorrentes'}
                </ActionButton>
                <ActionButton $primary onClick={handleCollectAll} disabled={isCollecting}>
                  {collecting.all ? <FaSpinner className="fa-spin" /> : <FaSync />}
                  {collecting.all ? 'Coletando Tudo...' : 'Coletar Tudo'}
                </ActionButton>
              </div>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '12px' }}>
                A coleta manual busca dados de todas as fontes configuradas. Em produção, isso pode ser agendado automaticamente.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <FaCog />
                Configurações de Monitoramento
              </CardTitle>
              <ActionButton $primary onClick={() => setShowConfigModal(true)}>
                <FaPlus /> Adicionar Monitoramento
              </ActionButton>
            </CardHeader>
            <CardContent>
              {configs.length === 0 ? (
              <EmptyState>
                <FaCog />
                <h3>Nenhum monitoramento configurado</h3>
                <p>Adicione keywords, queries de notícias e indicadores para monitorar.</p>
                <ActionButton $primary onClick={() => setShowConfigModal(true)}>
                  <FaPlus /> Configurar Monitoramento
                </ActionButton>
              </EmptyState>
            ) : (
              <ConfigList>
                {configs.map(config => (
                  <ConfigItem key={config.id} $active={config.is_active}>
                    <div className="info">
                      <span className="type">{config.config_type}</span>
                      <span className="name">{config.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span className="status">
                        {config.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                      <IconButton onClick={() => deleteConfig(config.id)} title="Remover">
                        <FaTrash />
                      </IconButton>
                    </div>
                  </ConfigItem>
                ))}
              </ConfigList>
            )}
          </CardContent>
          </Card>
        </>
      )}

      {/* Add Competitor Modal */}
      {showCompetitorModal && (
        <ModalOverlay onClick={() => setShowCompetitorModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h3>Adicionar Concorrente</h3>
              <IconButton onClick={() => setShowCompetitorModal(false)}>
                <FaTimes />
              </IconButton>
            </ModalHeader>
            <ModalContent>
              <FormGroup>
                <label>Nome da Empresa *</label>
                <input
                  type="text"
                  value={newCompetitor.name}
                  onChange={e => setNewCompetitor({ ...newCompetitor, name: e.target.value })}
                  placeholder="Ex: Empresa ABC"
                />
              </FormGroup>
              <FormGroup>
                <label>Website</label>
                <input
                  type="url"
                  value={newCompetitor.website}
                  onChange={e => setNewCompetitor({ ...newCompetitor, website: e.target.value })}
                  placeholder="https://www.empresa.com.br"
                />
              </FormGroup>
              <FormGroup>
                <label>LinkedIn</label>
                <input
                  type="url"
                  value={newCompetitor.linkedin_url}
                  onChange={e => setNewCompetitor({ ...newCompetitor, linkedin_url: e.target.value })}
                  placeholder="https://www.linkedin.com/company/..."
                />
              </FormGroup>
              <FormGroup>
                <label>Descrição</label>
                <textarea
                  value={newCompetitor.description}
                  onChange={e => setNewCompetitor({ ...newCompetitor, description: e.target.value })}
                  placeholder="Breve descrição do concorrente..."
                />
              </FormGroup>
            </ModalContent>
            <ModalFooter>
              <ActionButton onClick={() => setShowCompetitorModal(false)}>
                Cancelar
              </ActionButton>
              <ActionButton $primary onClick={addCompetitor} disabled={!newCompetitor.name}>
                <FaPlus /> Adicionar
              </ActionButton>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Add Config Modal */}
      {showConfigModal && (
        <ModalOverlay onClick={() => setShowConfigModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h3>Adicionar Monitoramento</h3>
              <IconButton onClick={() => setShowConfigModal(false)}>
                <FaTimes />
              </IconButton>
            </ModalHeader>
            <ModalContent>
              <FormGroup>
                <label>Tipo *</label>
                <select
                  value={newConfig.config_type}
                  onChange={e => setNewConfig({ ...newConfig, config_type: e.target.value })}
                >
                  <option value="keyword">Keyword (Google Trends)</option>
                  <option value="news_query">Query de Notícias</option>
                  <option value="indicator">Indicador Econômico</option>
                </select>
              </FormGroup>
              <FormGroup>
                <label>Nome *</label>
                <input
                  type="text"
                  value={newConfig.name}
                  onChange={e => setNewConfig({ ...newConfig, name: e.target.value })}
                  placeholder="Ex: automação industrial"
                />
              </FormGroup>
              <FormGroup>
                <label>Valor/Query</label>
                <input
                  type="text"
                  value={newConfig.value}
                  onChange={e => setNewConfig({ ...newConfig, value: e.target.value })}
                  placeholder="Termo de busca ou código do indicador"
                />
              </FormGroup>
              <FormGroup>
                <label>Frequência</label>
                <select
                  value={newConfig.check_frequency}
                  onChange={e => setNewConfig({ ...newConfig, check_frequency: e.target.value })}
                >
                  <option value="hourly">A cada hora</option>
                  <option value="daily">Diária</option>
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensal</option>
                </select>
              </FormGroup>
            </ModalContent>
            <ModalFooter>
              <ActionButton onClick={() => setShowConfigModal(false)}>
                Cancelar
              </ActionButton>
              <ActionButton $primary onClick={addConfig} disabled={!newConfig.name}>
                <FaPlus /> Adicionar
              </ActionButton>
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default MarketIntelligence;
